import { useState, useRef, useEffect } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { GROUP, IMAGES } from "../lib/site";

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
};

const Pillar = ({ p, i, open, onToggle }) => (
  <motion.div
    data-testid={`group-pillar-${i}`}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
    className={`group bg-[#070707]/80 backdrop-blur-sm relative overflow-hidden transition-colors duration-300 ${open ? "bg-[#0e0e0e]" : ""}`}
  >
    <button
      data-testid={`group-pillar-toggle-${i}`}
      onClick={onToggle}
      aria-expanded={open}
      className="w-full text-left p-8 md:p-9 relative"
    >
      <div className="absolute -right-6 -top-8 font-serif text-[7rem] leading-none text-white/[0.03] select-none group-hover:text-[#B87333]/10 transition-colors duration-500">
        0{i + 1}
      </div>
      <div className="relative z-10 flex items-start justify-between gap-4">
        <div>
          <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333]">{p.tag}</span>
          <h3 className="font-serif text-2xl text-[#F5F5F0] mt-2">{p.title}</h3>
        </div>
        <span className="grid place-items-center w-8 h-8 rounded-full border border-white/20 text-[#C9C9C2] shrink-0 group-hover:border-[#B87333] group-hover:text-[#B87333] transition-colors">
          {open ? <Minus size={15} /> : <Plus size={15} />}
        </span>
      </div>
      <p className="relative z-10 font-body text-sm text-[#8A8A82] mt-3 leading-relaxed">{p.desc}</p>
    </button>
    <motion.div
      initial={false}
      animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="overflow-hidden"
    >
      <p className="px-8 md:px-9 pb-8 md:pb-9 font-body text-sm text-[#C9C9C2] leading-relaxed border-t border-white/10 pt-5" data-testid={`group-pillar-detail-${i}`}>{p.detail}</p>
    </motion.div>
  </motion.div>
);

export const OurGroup = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="group" data-testid="group-section" className="relative py-24 md:py-36 border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={IMAGES.groupBanner} alt="" className="w-full h-full object-cover opacity-[0.14] grayscale" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707] via-[#070707]/70 to-[#070707]" />
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <span className="overline text-[#B87333]">{GROUP.kicker}</span>
            <h2 className="font-serif text-[clamp(2rem,5vw,4.5rem)] leading-[1.02] tracking-[-0.02em] mt-6">{GROUP.headline}</h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 font-body text-[#C9C9C2] leading-relaxed text-base md:text-lg"
          >
            {GROUP.body}
          </motion.p>
        </div>

        {/* Animated PAN-India counters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-16" data-testid="group-stats">
          {GROUP.stats.map((s) => (
            <div key={s.label} className="bg-[#070707]/85 backdrop-blur-sm px-6 py-8 md:px-8 md:py-10">
              <div className="font-serif text-4xl md:text-6xl tracking-[-0.02em] text-[#F5F5F0]">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mt-3">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Interactive entity pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-6">
          {GROUP.pillars.map((p, i) => (
            <Pillar key={p.title} p={p} i={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>

        {/* State coverage */}
        <div className="mt-16">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
            <span className="overline text-[#B87333]">/ PAN-India coverage</span>
            <span className="font-body text-xs text-[#6f6f68]">Entities floated State-by-State for local compliance</span>
          </div>
          <div className="flex flex-wrap gap-2.5" data-testid="group-states">
            {GROUP.states.map((st, i) => (
              <motion.span
                key={st}
                initial={{ opacity: 0, y: 12, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 12) * 0.03, ease: [0.16, 1, 0.3, 1] }}
                className="font-grotesk text-[11px] uppercase tracking-[0.14em] text-[#C9C9C2] border border-white/12 rounded-full px-4 py-2 hover:border-[#B87333] hover:text-[#F5F5F0] transition-colors duration-300"
              >
                {st}
              </motion.span>
            ))}
          </div>
        </div>

        <p className="font-body text-xs text-[#6f6f68] mt-10 max-w-2xl leading-relaxed">
          Registered entity details for each subsidiary — including GSTIN, CIN and correspondence address — are shared with verified trade partners on request and reflected on every GST tax invoice.
        </p>
      </div>
    </section>
  );
};
